import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { FeaturesTableV2 } from '@/features/features/components/FeaturesTableV2';
import { queryFeatures } from '@/features/features/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { buildContainsFilter, mergeFilters } from '@/ui/composites/grid/gridFilters';
import {
  featuresTableDefinition,
  featuresSortFieldMap,
  type FeaturesSortDirection,
  type FeaturesSortKey,
} from './featuresTableDefinition';
import type { TagOption } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { FeatureDto, ProjectDto, QueryResult } from '@flowqa/shared';

type FeaturesTablePanelProps = {
  fixedFilters: GridQueryInput['filters'];
  rangeStartDay: string | null;
  rangeEndDay: string | null;
  noRowsText: string;
  projectOptions?: Array<
    Pick<ProjectDto, 'badgeChars' | 'badgeColor' | 'badgeEnabled' | 'id' | 'name'>
  >;
  searchValue?: string;
  tagOptions?: TagOption[];
};

export const FeaturesTablePanel = ({
  fixedFilters,
  rangeStartDay,
  rangeEndDay,
  noRowsText,
  projectOptions = [],
  searchValue = '',
  tagOptions = [],
}: FeaturesTablePanelProps): ReactElement => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const [sortKey, setSortKey] = useState<FeaturesSortKey>(featuresTableDefinition.defaultSortKey);
  const [sortDirection, setSortDirection] = useState<FeaturesSortDirection>(
    featuresTableDefinition.defaultSortDirection,
  );
  const sort = useMemo(
    () => ({ field: sortKey, direction: sortDirection }),
    [sortDirection, sortKey],
  );
  const fixedFiltersKey = useMemo(() => JSON.stringify(fixedFilters), [fixedFilters]);
  const queryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<FeatureDto>> =>
      queryFeatures(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        filters: mergeFilters(input.filters, buildContainsFilter('name', searchValue)),
        sorts: input.sorts.map((inputSort) => ({
          field: featuresSortFieldMap[inputSort.field as FeaturesSortKey],
          direction: inputSort.direction,
        })),
      }),
    [searchValue, selectedOrganisationId, selectedProjectId],
  );

  return (
    <FeaturesTableV2
      fixedFilters={fixedFilters}
      queryFn={queryFn}
      queryKey={[
        ...queryKeys.dataGrid(gridPaths.features),
        selectedOrganisationId ?? null,
        selectedProjectId ?? null,
        sortKey,
        sortDirection,
        rangeStartDay,
        rangeEndDay,
        fixedFiltersKey,
      ]}
      noRowsText={noRowsText}
      projectOptions={projectOptions}
      search={searchValue}
      selectedProjectId={selectedProjectId}
      sort={sort}
      tagOptionsOverride={tagOptions}
      onSortChange={(nextSort) => {
        setSortKey(nextSort.field as FeaturesSortKey);
        setSortDirection(nextSort.direction as FeaturesSortDirection);
      }}
    />
  );
};
